import React, {useEffect, useState} from "react";
import {Badge, UncontrolledTooltip} from "reactstrap";
import ReactGA from "react-ga4";
import {usePayment} from "../../contexts/PaymentContext";
import {useNavigate} from "react-router-dom";
import {GoogleAnalyticsEventCategories} from "../../constants";

interface TokenBalanceHeaderProps {
  className?: string;
}

const TokenBalanceHeader: React.FC<TokenBalanceHeaderProps> = ({className = ""}) => {
  const {tokenBalance, isLoading, error, getTokenBalance} = usePayment();
  const navigate = useNavigate();
  const [showTooltip, setShowTooltip] = useState(false);

  // Auto-refresh balance on component mount
  useEffect(() => {
    if (!tokenBalance && !isLoading && !error) {
      getTokenBalance(false);
    }
  }, [tokenBalance, isLoading, error, getTokenBalance]);

  // Delay tooltip rendering to ensure target element exists
  useEffect(() => {
    if (tokenBalance) {
      const timer = setTimeout(() => {
        setShowTooltip(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [tokenBalance]);

  const getTotalAvailableTokens = (): number => {
    if (!tokenBalance) {
      return 0;
    }
    return (tokenBalance.tokens ?? 0) + (tokenBalance.freeUsageRemaining ?? 0);
  };

  const isLowBalance = (): boolean => {
    return getTotalAvailableTokens() <= 5;
  };

  const getFreeUsageColor = (remaining: number, total: number = 5): string => {
    const percentage = (remaining / total) * 100;
    if (percentage >= 40) {
      return "success";
    }
    if (percentage >= 20) {
      return "warning";
    }
    return "danger";
  };

  const formatCompactNumber = (num: number): string => {
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + "k";
    }
    return num.toString();
  };

  if (error || isLoading || !tokenBalance) {
    return (
      <div className={`token-balance-header ${className}`}>
        <div className="d-flex align-items-center">
          <i className="fa fa-coins text-muted mr-2" />
          <span className="text-muted">{error ? "Error" : isLoading ? "Loading..." : "---"}</span>
        </div>
      </div>
    );
  }

  const handleNavigateToPurchase = () => {
    // Refresh balance before navigation to ensure up-to-date data
    getTokenBalance(true);

    // Track navigation to purchase page from token balance
    ReactGA.event({
      category: GoogleAnalyticsEventCategories.PAYMENT,
      action: "navigate to purchase page",
      label: `from token balance - total tokens: ${getTotalAvailableTokens()}`
    });
    navigate("/tokens/purchase");
  };

  return (
    <div className={`token-balance-header ${className}`} onClick={handleNavigateToPurchase} id="token-balance-pill">
      <div className="d-flex align-items-center">
        {/* Free Tokens First - Use These First! */}
        <div className="d-flex align-items-center mr-3">
          <i className="fa fa-gift text-success mr-2" />
          <span className={`font-weight-bold text-${getFreeUsageColor(tokenBalance.freeUsageRemaining)}`}>
            {tokenBalance.freeUsageRemaining}
          </span>
          <span className="text-muted small ml-1">free</span>
          <div
            className="mini-progress-bar ml-2"
            style={{
              width: "20px",
              height: "3px",
              backgroundColor: "rgba(255,255,255,0.2)",
              borderRadius: "2px",
              overflow: "hidden"
            }}
          >
            <div
              style={{
                width: `${(tokenBalance.freeUsageRemaining / 5) * 100}%`,
                height: "100%",
                backgroundColor:
                  tokenBalance.freeUsageRemaining >= 3
                    ? "#00d4aa"
                    : tokenBalance.freeUsageRemaining >= 1
                      ? "#ff8a80"
                      : "#fd5d93",
                transition: "width 0.3s ease"
              }}
            />
          </div>
        </div>

        {/* Paid Tokens - Saved for Later */}
        <div className="d-flex align-items-center">
          <i className="fa fa-coins text-success mr-2" />
          <span className={`font-weight-bold ${isLowBalance() ? "text-warning" : "text-success"}`}>
            {formatCompactNumber(tokenBalance.tokens ?? 0)}
          </span>
          <span className="text-muted small ml-1">paid</span>
          {isLowBalance() && (
            <Badge color="warning" className="ml-2" style={{fontSize: "0.6rem"}}>
              Low
            </Badge>
          )}
        </div>

        {/* Enhanced Tooltip */}
        {showTooltip && tokenBalance && (
          <UncontrolledTooltip target="token-balance-pill" placement="bottom" className="token-tooltip">
            <div>
              <strong>Token Usage Order (Free First!)</strong>
              <br />
              <span style={{color: "#28a745"}}>
                <i className="fa fa-gift" /> Free tokens: {tokenBalance.freeUsageRemaining} of 5 remaining
              </span>
              <br />
              <span style={{color: "#28a745"}}>
                <i className="fa fa-coins" /> Paid tokens: {tokenBalance.tokens ?? 0}
              </span>
              <br />
              <br />
              <em>Free tokens are used first!</em>
              <br />
              {tokenBalance.nextResetDate && (
                <>
                  Free tokens reset: {new Date(tokenBalance.nextResetDate).toISOString().split("T")[0]}
                  <br />
                </>
              )}
              <small style={{color: "#ced4da"}}>Click to purchase more tokens</small>
            </div>
          </UncontrolledTooltip>
        )}
      </div>
    </div>
  );
};

export default TokenBalanceHeader;
